import type { TFunction } from "i18next";
import { startCase } from "lodash";
import type { IChatShortcutItem } from "@/types/(my-space)/chat.interface";

/** The `useLocale()` formatters this needs — passed in so it stays pure. */
export interface ShortcutFormatters {
  formatDateRange: (start: Date, end: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatCurrency: (value: number, currency: string) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The API's date text → a Date. A bare `yyyy-MM-dd` is a calendar day and is
 * read at LOCAL midnight (`new Date("2026-10-12")` is UTC and slips a day west
 * of Greenwich). A Postgres timestamp ("2026-10-12 09:00:00+00") is made ISO.
 */
export const parseShortcutDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const day = DATE_ONLY.exec(value.trim());
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  const iso = value
    .trim()
    .replace(" ", "T")
    .replace(/([+-]\d{2})$/, "$1:00");
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The year only when it isn't this one — the card is narrow. */
const dayOptions = (dates: Date[], now: Date): Intl.DateTimeFormatOptions =>
  dates.every((d) => d.getFullYear() === now.getFullYear())
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };

/** "Oct 20 – 23", or one date when it's a single day (or only one is known). */
export const formatShortcutRange = (
  fmt: Pick<ShortcutFormatters, "formatDateRange">,
  startText: string | null,
  endText: string | null,
  now: Date,
): string | null => {
  const start = parseShortcutDate(startText) ?? parseShortcutDate(endText);
  if (!start) return null;
  const end = parseShortcutDate(endText) ?? start;
  const [a, b] = end < start ? [start, start] : [start, end];
  return fmt.formatDateRange(a, b, dayOptions([a, b], now));
};

const formatDay = (fmt: ShortcutFormatters, text: string | null, now: Date) =>
  formatShortcutRange(fmt, text, null, now);

const formatAmount = (fmt: ShortcutFormatters, item: IChatShortcutItem) => {
  if (item.amount == null) return null;
  return item.currency
    ? fmt.formatCurrency(item.amount, item.currency)
    : fmt.formatNumber(item.amount, { maximumFractionDigits: 2 });
};

/**
 * A Latin request number inside an Arabic line would otherwise pull the date
 * next to it into its own run ("…26 أكتوبر DOC-0042 · 15"): isolate it.
 */
const isolate = (text: string | null) => (text ? `\u2068${text}\u2069` : null);

/**
 * The quieter line under an item's title, per kind: the dates of a leave or an
 * event, an expense's amount and day, a request's number and day, the day an
 * announcement went out. Empty parts are dropped.
 */
export const shortcutSubtitle = (
  item: IChatShortcutItem,
  fmt: ShortcutFormatters,
  now: Date = new Date(),
): string => {
  const parts: (string | null)[] = (() => {
    switch (item.type) {
      case "leave_request":
      case "event":
        return [formatShortcutRange(fmt, item.startDate, item.endDate, now)];
      case "expense":
        return [formatAmount(fmt, item), formatDay(fmt, item.startDate, now)];
      case "document_request":
      case "asset_request":
        return [isolate(item.reference), formatDay(fmt, item.startDate, now)];
      case "announcement":
        return [formatDay(fmt, item.startDate, now)];
      case "task":
        // Its key and when it's due.
        return [isolate(item.reference), formatDay(fmt, item.endDate, now)];
      default:
        return [];
    }
  })();
  return parts.filter((p): p is string => !!p).join(" · ");
};

/**
 * What an item's status badge reads. A ticket's status is its board column —
 * a name the company chose ("QA", "In Review") — so it's shown as written.
 */
export const shortcutItemStatus = (t: TFunction, item: Pick<IChatShortcutItem, "type" | "status">) =>
  item.status ? (item.type === "task" ? item.status : shortcutStatusLabel(t, item.status)) : null;

/** API status enum → `common:status.*`, falling back to readable English. */
export const shortcutStatusLabel = (t: TFunction, status: string) => {
  // "in-review" and "In Review" both mean in_review.
  const key = status.toLowerCase().replace(/[\s-]+/g, "_");
  return t(`common:status.${key}`, { defaultValue: startCase(key.replace(/_/g, " ")) });
};
